import { motion } from "framer-motion";
import {
    getEntryGatePosition,
    getExitGatePosition
} from "../utils/parkingUtils";

export default function Vehicle({
    vehicle,
    moving = false
}) {

    // =========================================
    // STATUS
    // =========================================

    const isEntering =
        vehicle.status === "moving";

    const isExiting =
        vehicle.status === "exiting";

    // =========================================
    // VEHICLE IMAGE
    // =========================================

    const vehicleImage =
        "/vehicles/car-top-view.png";

    // =========================================
    // VEHICLE SIZE
    // =========================================

    const VEHICLE_WIDTH = 58;

    const VEHICLE_HEIGHT = 32;

    // =========================================
    // CENTER -> TOP LEFT
    // parkingUtils gives CENTER coordinates
    // =========================================

    const finalX =
        vehicle.x - (VEHICLE_WIDTH / 2);

    const finalY =
        vehicle.y - (VEHICLE_HEIGHT / 2);

    // =========================================
    // =========================================
    // GATE POSITIONS
    // =========================================

    const entryGate = getEntryGatePosition();
    const exitGate = getExitGatePosition();

    // =========================================
    // ANIMATION START
    // =========================================

    const initialX = isEntering
        ? entryGate.x - (VEHICLE_WIDTH / 2)
        : finalX;

    const initialY = isEntering
        ? entryGate.y - (VEHICLE_HEIGHT / 2)
        : finalY;

    // =========================================
    // ANIMATION TARGET
    // =========================================

    const targetX = isExiting
        ? exitGate.x - (VEHICLE_WIDTH / 2)
        : finalX;

    const targetY = isExiting
        ? exitGate.y - (VEHICLE_HEIGHT / 2)
        : finalY;

    // =========================================
    // FORMAT DATE
    // 20260514 -> 14-05-2026
    // =========================================

    const formatDate = (dateKey) => {

        if (!dateKey) return "";

        const str = String(dateKey);

        if (str.length !== 8) {

            return str;
        }

        const year =
            str.slice(0, 4);

        const month =
            str.slice(4, 6);

        const day =
            str.slice(6, 8);

        return `${day}-${month}-${year}`;
    };

    // =========================================
    // FORMAT TIME
    // 143522 -> 14:35:22
    // =========================================

    const formatTime = (timeKey) => {

        if (!timeKey) return "";

        const str =
            String(timeKey)
                .padStart(6, "0");

        if (str.length !== 6) {

            return str;
        }

        const hh =
            str.slice(0, 2);

        const mm =
            str.slice(2, 4);

        const ss =
            str.slice(4, 6);

        return `${hh}:${mm}:${ss}`;
    };

    return (

        <motion.g

            initial={{
                x: initialX,
                y: initialY,
                opacity: 0,
                scale: 0.8
            }}

            animate={(() => {
                if (isEntering) {
                    return {
                        x: [initialX, targetX, targetX],
                        y: [initialY, initialY, finalY],
                        opacity: 1,
                        scale: 1
                    };
                }

                if (isExiting) {
                    return {
                        x: [finalX, finalX, targetX],
                        y: [finalY, exitGate.y - (VEHICLE_HEIGHT / 2), targetY],
                        opacity: 1,
                        scale: 1
                    };
                }

                return {
                    x: targetX,
                    y: targetY,
                    opacity: 1,
                    scale: 1
                };
            })()}

            exit={{
                opacity: 0,
                scale: 0.5
            }}

            transition={{
                duration: moving ? 3 : 0.5,
                ease: "easeInOut",
                times: isEntering || isExiting ? [0, 0.45, 1] : undefined
            }}
        >

            {/* ================================= */}
            {/* VEHICLE SHADOW */}
            {/* ================================= */}



            {/* ================================= */}
            {/* REAL VEHICLE IMAGE */}
            {/* ================================= */}

            <image
                href={vehicleImage}
                width={VEHICLE_WIDTH}
                height={VEHICLE_HEIGHT}
                preserveAspectRatio="xMidYMid meet"
            />

            {/* ================================= */}
            {/* LICENSE PLATE */}
            {/* ================================= */}

            <text
                x={VEHICLE_WIDTH / 2}
                y="2"
                textAnchor="middle"
                fontSize="8"
                fill="#334155"
                fontWeight="700"
            >
                {vehicle.plate}
            </text>

            {/* ================================= */}
            {/* ENTRY DATE - LEFT SIDE */}
            {/* ================================= */}

            {vehicle.entry_date_key && (

                <text
                    x="-14"
                    y="14"
                    textAnchor="end"
                    fontSize="8"
                    fill="#475569"
                    fontWeight="700"
                >
                    {formatDate(
                        vehicle.entry_date_key
                    )}
                </text>

            )}

            {/* ================================= */}
            {/* ENTRY TIME - LEFT SIDE */}
            {/* ================================= */}

            {vehicle.entry_time_key && (

                <text
                    x="-14"
                    y="24"
                    textAnchor="end"
                    fontSize="8"
                    fill="#475569"
                    fontWeight="700"
                >
                    {formatTime(
                        vehicle.entry_time_key
                    )}
                </text>

            )}

            {/* ================================= */}
            {/* MOVEMENT GLOW */}
            {/* ================================= */}

            {moving && (

                <motion.ellipse

                    cx={VEHICLE_WIDTH / 2}

                    cy={VEHICLE_HEIGHT / 2}

                    rx="38"

                    ry="22"

                    initial={{
                        opacity: 0.4,
                        scale: 0.8
                    }}

                    animate={{
                        opacity: 0,
                        scale: 1.6
                    }}

                    transition={{
                        repeat: Infinity,
                        duration: 1.5
                    }}

                    fill="rgba(59,130,246,0.35)"
                />

            )}

        </motion.g>
    );
}